import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Select,
  TextDescription,
  Button,
  Typography,
} from "@ann/web-components";
import { useParams } from "react-router-dom";
import { useFactsContext, useSuggestionsContext } from "../../../../context";
import { createSuggestion } from "../../../../../../services";
import { useMediaQueries } from "../../../../../../../../../utils/mediaQueries";
import {
  ModalContent,
  FormField,
  HelperText,
} from "./CreateSuggestionModal.styled";

const CreateSuggestionModal = ({
  isOpen,
  onClose,
  initialFact = null, // { fact_uuid, title } cuando viene de Facts
  lockFact = false, // true cuando viene desde FactDetail (no permite cambiar el fact)
}) => {
  const { knowledgeId } = useParams();
  const { baseFacts } = useFactsContext();
  const { setAlert, fetchSuggestions } = useSuggestionsContext();
  const [selectedFactUuid, setSelectedFactUuid] = useState(null);
  const [suggestedFact, setSuggestedFact] = useState("");
  const [rationale, setRationale] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return "95%";
    }
    return "600px";
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  // Los facts se cargan automáticamente cuando se monta Suggestions
  // No es necesario cargarlos aquí

  // Pre-seleccionar fact si viene de Facts page
  useEffect(() => {
    if (isOpen && initialFact) {
      setSelectedFactUuid(initialFact.fact_uuid);
      setSuggestedFact("");
      setRationale("");
      setError(null);
    } else if (isOpen && !initialFact) {
      // Reset cuando se abre desde "Add suggestion"
      setSelectedFactUuid(null);
      setSuggestedFact("");
      setRationale("");
      setError(null);
    }
  }, [isOpen, initialFact]);

  const handleFactChange = useCallback((value) => {
    setSelectedFactUuid(value);
    setError(null);
  }, []);

  const handleSuggestedFactChange = useCallback((value) => {
    setSuggestedFact(value);
    setError(null);
  }, []);

  const handleRationaleChange = useCallback((value) => {
    setRationale(value);
    setError(null);
  }, []);

  const validateForm = useCallback(() => {
    if (!selectedFactUuid) {
      setError("Please select a fact");
      return false;
    }
    if (!suggestedFact.trim()) {
      setError("Suggested fact is required");
      return false;
    }
    if (!rationale.trim()) {
      setError("Rationale/comment is required");
      return false;
    }
    return true;
  }, [selectedFactUuid, suggestedFact, rationale]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createSuggestion({
        fact_uuid: selectedFactUuid,
        value_text: suggestedFact.trim(),
        comment: rationale.trim(),
      });

      // Reset form
      setSelectedFactUuid(null);
      setSuggestedFact("");
      setRationale("");
      setError(null);

      // Cerrar modal siempre (éxito o error)
      onClose();

      // Refrescar la lista de sugerencias después de crear exitosamente
      if (knowledgeId) {
        await fetchSuggestions(
          {
            knowledgebase_uuid: knowledgeId,
            page: 1,
            page_size: 20,
            order_by: 'created_at',
            order_direction: 'DESC',
          },
          { persistAsBase: true }
        );
      }

      // Mostrar alert de éxito en el contexto
      setAlert({
        title: 'Success',
        description: 'Suggestion created successfully.',
        severity: 'success',
      });
    } catch (err) {
      console.error("Error creating suggestion:", err);
      
      // Cerrar modal siempre (éxito o error)
      onClose();

      // Mostrar alert de error en el contexto
      setAlert({
        title: 'Error',
        description: err.message || 'Failed to create suggestion. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedFactUuid, suggestedFact, rationale, validateForm, onClose, setAlert, knowledgeId, fetchSuggestions]);

  const handleCancel = useCallback(() => {
    setSelectedFactUuid(null);
    setSuggestedFact("");
    setRationale("");
    setError(null);
    onClose();
  }, [onClose]);

  // Limpiar error cuando el modal se cierra
  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const isFormValid = useMemo(() => {
    return selectedFactUuid && suggestedFact.trim() && rationale.trim();
  }, [selectedFactUuid, suggestedFact, rationale]);

  // Calcular el título del fact seleccionado con fallback a initialFact
  const selectedFactTitle = useMemo(() => {
    if (!selectedFactUuid) return undefined;
    
    // Buscar usando ambos formatos (factUuid y fact_uuid)
    const factFromBase = baseFacts.find((fact) => {
      const factUuid = fact.factUuid || fact.fact_uuid;
      return factUuid === selectedFactUuid;
    });
    
    if (factFromBase) {
      return factFromBase.title || factFromBase.factName || "Untitled Fact";
    }
    
    // Fallback: usar el título de initialFact si coincide
    if (initialFact && initialFact.fact_uuid === selectedFactUuid && initialFact.title) {
      return initialFact.title;
    }
    
    return undefined;
  }, [baseFacts, selectedFactUuid, initialFact]);

  // Determinar si el fact está bloqueado (prop explícita)
  const isFactLocked = lockFact;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="medium"
      width={modalWidth}
    >
      <Modal.Header>
        <Modal.Header.Title>Suggest an Update</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          {!isFactLocked && (
            <FormField>
              <Typography category="body" size="large" weight="bold">
                Select Fact *
              </Typography>
              <Select
                value={selectedFactTitle || " select a fact"}
                onChange={handleFactChange}
                placeholder="Select a fact to suggest an update..."
                size="small"
                state={error && !selectedFactUuid ? "error" : "default"}
              >
                <Select.Menu>
                  {baseFacts.map((fact) => {
                    const factUuid = fact.factUuid || fact.fact_uuid;
                    const factTitle = fact.title || fact.factName || "Untitled Fact";
                    return (
                      <Select.Item 
                        key={factUuid} 
                        value={factUuid} 
                        label={factTitle}
                      >
                        {factTitle}
                      </Select.Item>
                    );
                  })}
                </Select.Menu>
              </Select>
              {!selectedFactUuid && (
                <HelperText>
                  Please select a fact to continue.
                </HelperText>
              )}
            </FormField>
          )}

          <FormField>
            <Typography category="body" size="large" weight="bold">
              Suggested Fact *
            </Typography>
            <TextDescription
              label=""
              value={suggestedFact}
              onChange={handleSuggestedFactChange}
              placeholder="Enter your suggested Fact to replace or modify the selected Fact..."
              height="120px"
              size="small"
              state={error && !suggestedFact.trim() ? "error" : "default"}
            />
          </FormField>

          <FormField>
            <Typography category="body" size="large" weight="bold">
              Rationale / Comment *
            </Typography>
            <TextDescription
              label=""
              value={rationale}
              onChange={handleRationaleChange}
              placeholder="Explain why this change is needed and provide supporting details..."
              height="120px"
              size="small"
              state={error && !rationale.trim() ? "error" : "default"}
            />
            <HelperText>
              Provide context, sources, or reasoning to help reviewers evaluate
              the suggestion.
            </HelperText>
          </FormField>

          {error && (
            <Typography category="body" size="large" weight="bold">
              {error}
            </Typography>
          )}
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Button
          label="Cancel"
          size="medium"
          type="secondary"
          onClick={handleCancel}
          disabled={loading}
        />
        <Button
          label="Send Suggestion"
          size="medium"
          type="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CreateSuggestionModal;
